// src/auth/AuthProvider.tsx
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { MsalProvider } from "@azure/msal-react";
import { EventType } from "@azure/msal-browser";
import type { AccountInfo, AuthenticationResult } from "@azure/msal-browser";
import { msalInstance } from "../msalInstance";
import { AuthContext, type AuthContextType } from "./useAuth";
import { loginRequest } from "../msalConfig";
import { clearEngagementState, CLEAR_ON_SIGNOUT_PROMPT } from "../utils/clearEngagementState";

const InnerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Lazy initial state: by the time this renders, index.tsx bootstrap() has
  // already awaited handleRedirectPromise(), so the account is in MSAL cache.
  // Reading it here means the very first render already knows the session —
  // no flash of /login and no blank /home race.
  const [account, setAccount] = useState<AccountInfo | null>(
    () => msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null
  );

  useEffect(() => {
    const existing =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (existing) {
      msalInstance.setActiveAccount(existing);
      setAccount(existing);
    } else {
      console.log("ℹ️ No cached account on init. User not signed in yet.");
    }

    // Keep React account state in sync with MSAL login/logout events.
    const callbackId = msalInstance.addEventCallback((event) => {
      const payloadAccount = (event.payload as AuthenticationResult)?.account;
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        payloadAccount
      ) {
        msalInstance.setActiveAccount(payloadAccount);
        setAccount(payloadAccount);
      }
      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setAccount(null);
      }
    });

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, []);

  const login = (): void => {
    console.log("👉 Starting loginRedirect...");
    // Redirect-based login (no popup) to avoid COOP issues
    msalInstance.loginRedirect(loginRequest);
  };

  const logout = (): void => {
    // Same reasoning as Header.logout — see clearEngagementState.
    if (!window.confirm(CLEAR_ON_SIGNOUT_PROMPT)) return;
    clearEngagementState();
    console.log("👉 Starting logoutRedirect...");
    msalInstance.logoutRedirect();
  };

  const contextValue: AuthContextType = useMemo(
    () => ({ account, login, logout }),
    [account]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <MsalProvider instance={msalInstance}>
    <InnerAuthProvider>{children}</InnerAuthProvider>
  </MsalProvider>
);

export default AuthProvider;
